import type { AnalysisConfig, Building, Placement } from '../../core/domain/types';
import { MODELS } from '../../core/domain/types';
import type { CellInfo, QueueState, ResultEntry } from '../../core/worker/protocol';
import { evaluateHints } from '../../core/calc/hints';
import { METRICS } from '../../app/metrics';
import { fmt, fmtDate, fmtDateTime, fmtDuration } from '../../app/format';
import { useApp, workerControl } from '../../app/store';
import { GWR_CODE_SOURCE, gwrLabel } from '../../geo/services';
import { IconClose, IconInfo, IconPause, IconPlay, IconRetry, IconWarn } from '../icons';

// ---------------------------------------------------------------- Meldungen
export function Notices() {
  const notices = useApp((s) => s.notices);
  const dismiss = useApp((s) => s.dismiss);
  if (!notices.length) return null;
  return (
    <div style={{ position: 'fixed', right: 16, bottom: 88, zIndex: 60, display: 'flex', flexDirection: 'column', gap: 8, maxWidth: 420 }} aria-live="polite">
      {notices.map((n) => (
        <div key={n.id} className={`notice notice-${n.kind}`} role={n.kind === 'danger' ? 'alert' : 'status'} style={{ boxShadow: 'var(--shadow)' }}>
          {n.kind === 'info' ? <IconInfo /> : <IconWarn />}
          <div className="notice-body">
            <div className="notice-title">{n.title}</div>
            {n.text && <div className="small">{n.text}</div>}
          </div>
          <button type="button" className="icon-btn" style={{ width: 32, height: 32 }} aria-label="Meldung schliessen" onClick={() => dismiss(n.id)}>
            <IconClose />
          </button>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------- Abrufstatus
export function queueText(q: QueueState | null, mode: string, buildingCount = 0): { text: string; tone: 'ok' | 'warn' | 'danger' | 'muted' } {
  if (!q) return { text: 'Rechenkern startet …', tone: 'muted' };
  const pct = q.chunksTotal ? Math.round((q.chunksDone / q.chunksTotal) * 100) : 0;
  switch (q.state) {
    case 'leer':
      return { text: buildingCount ? 'Keine Abrufe ausstehend' : 'Keine Gebäude', tone: 'muted' };
    case 'fertig':
      return { text: mode === 'demo' ? 'Snapshot geladen' : 'Alle Daten geladen', tone: 'ok' };
    case 'pausiert':
      return { text: `Abruf pausiert · ${pct} %`, tone: 'warn' };
    case 'limit':
      return { text: `${q.pauseKind === 'plan' ? 'Gedrosselt · weiter' : 'Abruflimit · Fortsetzung'} ${q.pauseUntil ? `um ${new Date(q.pauseUntil).toLocaleTimeString('de-CH', { hour: '2-digit', minute: '2-digit' })}` : 'geplant'}`, tone: 'warn' };
    case 'fehler':
      return { text: q.chunksFailed ? `Abruf unvollständig · ${q.chunksFailed} Jahresstücke fehlgeschlagen` : 'Datenanbieter nicht erreichbar · Abruf angehalten', tone: 'danger' };
    default:
      return { text: q.placementsPending ? `Rasterzellen werden zugeordnet (${q.placementsPending})` : `Daten laden · ${pct} % · ${fmtDuration(q.etaMs)}`, tone: 'muted' };
  }
}

export function QueueBar() {
  const q = useApp((s) => s.queue);
  const mode = useApp((s) => s.mode);
  const count = useApp((s) => s.buildings.length);
  const t = queueText(q, mode, count);
  const pct = q && q.chunksTotal ? (q.chunksDone / q.chunksTotal) * 100 : 0;
  const busy = q && (q.state === 'laedt' || q.state === 'limit' || q.state === 'pausiert');
  return (
    <div className="queue-bar" role="status" aria-live="polite">
      {q && q.chunksTotal > 0 && q.state !== 'fertig' && (
        <div className="progress" aria-hidden="true">
          <span style={{ width: `${pct}%`, background: t.tone === 'danger' ? 'var(--danger)' : t.tone === 'warn' ? 'var(--warn)' : undefined }} />
        </div>
      )}
      <span className="hide-mobile queue-text" title={t.text} style={{ color: t.tone === 'danger' ? 'var(--danger)' : t.tone === 'warn' ? 'var(--warn)' : undefined }}>
        {t.text}
      </span>
      {busy && q.state !== 'pausiert' && mode === 'live' && (
        <button type="button" className="icon-btn" aria-label="Datenabruf pausieren" title="Datenabruf pausieren" onClick={workerControl.pause}>
          <IconPause />
        </button>
      )}
      {q && (q.state === 'pausiert' || q.state === 'limit') && (
        <button type="button" className="icon-btn" aria-label="Datenabruf fortsetzen" title="Datenabruf fortsetzen" onClick={workerControl.resume}>
          <IconPlay />
        </button>
      )}
      {q && q.state === 'fehler' && (
        <button type="button" className="icon-btn" aria-label="Datenabruf erneut versuchen" title="Erneut versuchen" onClick={workerControl.retry}>
          <IconRetry />
        </button>
      )}
    </div>
  );
}

/** Ausführliche Zustandsmeldung für Rate-Limit, Ausfall und Pause. */
export function QueueNotice() {
  const q = useApp((s) => s.queue);
  const mode = useApp((s) => s.mode);
  if (!q || mode === 'demo') return null;
  if (q.state === 'limit') {
    const plan = q.pauseKind === 'plan';
    return (
      <div className={`notice ${plan ? 'notice-info' : 'notice-warn'}`} role="status">
        {plan ? <IconInfo /> : <IconWarn />}
        <div className="notice-body">
          <div className="notice-title">{plan ? 'Datenabruf gedrosselt' : 'Abruflimit der Datenquelle erreicht'}</div>
          <div className="small">
            {q.message ?? 'Die freie Open-Meteo-API begrenzt die Abrufmenge.'} {plan ? 'Die App verteilt die Abrufe, um die Grenzen der freien API einzuhalten.' : ''} Bereits geladene Jahre bleiben gültig und werden angezeigt; der Abruf setzt
            {q.pauseUntil ? ` um ${new Date(q.pauseUntil).toLocaleTimeString('de-CH', { hour: '2-digit', minute: '2-digit' })}` : ''} automatisch fort. Es werden keine Beispieldaten eingesetzt.
          </div>
        </div>
        {!plan && (
          <button type="button" className="btn btn-sm" onClick={workerControl.resume}>
            Jetzt versuchen
          </button>
        )}
      </div>
    );
  }
  if (q.state === 'fehler') {
    return (
      <div className="notice notice-danger" role="alert">
        <IconWarn />
        <div className="notice-body">
          <div className="notice-title">Daten konnten nicht vollständig geladen werden</div>
          <div className="small">
            {q.lastError ?? 'Der Datenanbieter ist nicht erreichbar.'} Fehlende Jahre erscheinen als Lücke; es werden keine Beispieldaten eingesetzt.
          </div>
        </div>
        <button type="button" className="btn btn-sm" onClick={workerControl.retry}>
          <IconRetry /> Erneut versuchen
        </button>
      </div>
    );
  }
  if (q.state === 'pausiert') {
    return (
      <div className="notice notice-info" role="status">
        <IconPause />
        <div className="notice-body">
          <div className="notice-title">Datenabruf pausiert</div>
          <div className="small">Bereits geladene Jahre werden ausgewertet; noch fehlende Jahre erscheinen als «lädt».</div>
        </div>
        <button type="button" className="btn btn-sm" onClick={workerControl.resume}>
          <IconPlay /> Fortsetzen
        </button>
      </div>
    );
  }
  if (q.lastError && q.state === 'laedt') {
    return (
      <div className="notice notice-warn" role="status">
        <IconWarn />
        <div className="notice-body">
          <div className="notice-title">Vorübergehende Störung</div>
          <div className="small">{q.lastError}</div>
        </div>
      </div>
    );
  }
  return null;
}

// ---------------------------------------------------------------- Datenqualität
export function QualityPanel({ result, placement, cell, cfg, building }: { result?: ResultEntry; placement?: Placement; cell?: CellInfo; cfg: AnalysisConfig; building: Building }) {
  const mode = useApp((s) => s.mode);
  if (!placement) {
    return (
      <div className="panel">
        <h3>Datenqualität</h3>
        <p className="small muted" style={{ marginTop: 8 }}>
          Rasterzelle wird zugeordnet …
        </p>
      </div>
    );
  }
  const inPeriod = result ? result.years.filter((y) => y.year >= cfg.periodStart && y.year <= cfg.periodEnd) : [];
  const complete = inPeriod.filter((y) => y.complete).length;
  const partial = inPeriod.filter((y) => !y.complete && y.daysValid > 0);
  const pending = inPeriod.filter((y) => !y.loaded && y.daysValid === 0).length;
  const missing = inPeriod.reduce((a, y) => a + y.hoursMissing, 0);
  const conflicts = inPeriod.reduce((a, y) => a + y.hoursConflict, 0);
  const ref = result?.reference;
  const latestChunk = cell?.chunks.length ? cell.chunks.reduce((a, c) => (c.retrievedAt > a.retrievedAt ? c : a)) : null;
  const corr = result?.heightCorrectionK ?? 0;
  return (
    <div className="panel">
      <h3>Datenqualität und Herkunft</h3>
      <dl className="dl" style={{ marginTop: 12 }}>
        <dt>Quelle</dt>
        <dd>
          {MODELS[placement.model].label} ({MODELS[placement.model].resolution}), Reanalyse über Open-Meteo
          {mode === 'demo' && <span className="chip chip-demo" style={{ marginLeft: 6 }}>Snapshot</span>}
        </dd>
        <dt>Vollständige Jahre</dt>
        <dd>
          {complete} von {inPeriod.length} ({cfg.periodStart}–{cfg.periodEnd}){pending > 0 && ` · ${pending} laden noch`}
        </dd>
        <dt>Referenz</dt>
        <dd>
          {ref ? (
            ref.status === 'voll' ? (
              `${ref.yearsValid.length} von ${ref.yearsTotal} Jahren`
            ) : ref.status === 'teil' ? (
              <>
                <span className="tag tag-warm">Teilreferenz</span> {ref.yearsValid.length} von {ref.yearsTotal} Jahren
              </>
            ) : (
              <>
                <span className="tag tag-warm">Keine Referenzkennzahl</span> {ref.yearsValid.length} von {ref.yearsTotal} Jahren (&lt; 20)
              </>
            )
          ) : (
            '–'
          )}
        </dd>
        <dt>Lücken</dt>
        <dd>
          {fmt(missing)} fehlende Stundenwerte{conflicts ? `, ${fmt(conflicts)} widersprüchliche` : ''}
          {partial.length > 0 && ` · Teiljahre: ${partial.map((y) => y.year).join(', ')}`}
        </dd>
        <dt>Rasterzelle</dt>
        <dd>
          {placement.cellLat.toFixed(2)}° N, {placement.cellLon.toFixed(2)}° E{cell?.elevation != null ? ` · Zellhöhe ${fmt(cell.elevation)} m` : ''}
        </dd>
        <dt>Höhenkorrektur</dt>
        <dd>
          {cfg.heightCorrection ? `${corr >= 0 ? '+' : '−'}${fmt(Math.abs(corr), 2)} K auf ${fmt(placement.targetElevation)} m (${building.elevation?.source === 'swissALTI3D' ? 'swissALTI3D' : 'Open-Meteo 90-m-Modell'}), 0.0065 K/m` : 'ausgeschaltet (Zellwerte)'}
        </dd>
        <dt>Zeitzone</dt>
        <dd>{placement.timezone}</dd>
        <dt>Abruf</dt>
        <dd>{latestChunk ? `${fmtDateTime(latestChunk.retrievedAt)} (${latestChunk.origin})` : '–'}</dd>
      </dl>
      <p className="xsmall muted" style={{ marginTop: 12 }}>
        Reanalyse ist eine modellgestützte Rekonstruktion der Aussenluft für eine Rasterzelle, keine Messung am Gebäude. Lokale Verschattung und städtische Wärmeinseln sind nur begrenzt abgebildet.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------- Hinweise
export function HintsPanel({ result, cfg, compact }: { result?: ResultEntry; cfg: AnalysisConfig; compact?: boolean }) {
  if (!result) return null;
  const ev = evaluateHints(result, cfg);
  return (
    <div className="panel">
      <h3>Hinweise für weitere Untersuchungen</h3>
      <p className="xsmall muted" style={{ marginTop: 4 }}>
        Regelbasiert aus den Klimakennzahlen. Schwellen sind Projektannahmen (Annahmen › Hinweisregeln), keine Normanforderungen und keine Betriebsfreigabe.
      </p>
      <div style={{ marginTop: 16 }}>
        {!ev.sufficient && <p className="small muted">{ev.reason}. Hinweise erscheinen, sobald genügend Jahre geladen sind.</p>}
        {ev.sufficient && ev.hints.length === 0 && <p className="small muted">Keine Regel ausgelöst ({ev.recentYears[0]}–{ev.recentYears[ev.recentYears.length - 1]}).</p>}
        {ev.hints.map((h) => {
          const meta = METRICS[h.trigger.metric];
          return (
            <div key={h.id} className="hint">
              <div className="hint-title">
                <span className={`tag ${h.tone === 'warm' ? 'tag-warm' : h.tone === 'cool' ? 'tag-cool' : ''}`}>{h.tone === 'cool' ? 'Potenzial' : 'Prüfen'}</span>
                <span>{h.title}</span>
              </div>
              <div className="hint-trigger">
                Auslöser: {meta.label} {h.trigger.period}: <strong>{fmt(h.trigger.value, h.trigger.unit.startsWith('%') ? 0 : meta.refDecimals)} {h.trigger.unit}</strong>
                {h.trigger.reference && (
                  <>
                    {' '}
                    · {h.trigger.reference.period}: {fmt(h.trigger.reference.value, meta.refDecimals)} {h.trigger.unit}
                  </>
                )}
              </div>
              {!compact && (
                <>
                  <div className="hint-needs">Bedingung: {h.condition}.</div>
                  <div className="hint-needs">Für eine vertiefte Beurteilung nötig: {h.needs.join(', ')}.</div>
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- GWR
export function GwrPanel({ building }: { building: Building }) {
  const g = building.gwr;
  const mode = useApp((s) => s.mode);
  if (!building.egid && !g) return null;
  if (!g && mode === 'demo') {
    return (
      <div className="panel">
        <h3>Gebäudemerkmale (GWR)</h3>
        <p className="small muted" style={{ marginTop: 8 }}>
          EGID {building.egid}. Die GWR-Abfrage bei geo.admin.ch ist im Offline-Demomodus nicht verfügbar.
        </p>
      </div>
    );
  }
  return (
    <div className="panel">
      <h3>Gebäudemerkmale (GWR)</h3>
      {!g ? (
        <p className="small muted" style={{ marginTop: 8 }}>
          EGID {building.egid}: Merkmale werden bei geo.admin.ch abgefragt …
        </p>
      ) : (
        <>
          <dl className="dl" style={{ marginTop: 12 }}>
            <dt>EGID</dt>
            <dd>{g.egid}</dd>
            <dt>Kategorie</dt>
            <dd>{gwrLabel('GKAT', g.gkat)}</dd>
            <dt>Klasse</dt>
            <dd>{gwrLabel('GKLAS', g.gklas)}</dd>
            <dt>Baujahr</dt>
            <dd>{g.gbauj ?? gwrLabel('GBAUP', g.gbaup)}</dd>
            <dt>Geschosse</dt>
            <dd>{g.gastw ?? 'nicht erfasst'}</dd>
            <dt>Energiebezugsfläche</dt>
            <dd>{g.gebf ? `${fmt(g.gebf)} m²` : 'nicht erfasst'}</dd>
            <dt>Heizung</dt>
            <dd>
              {gwrLabel('GWAERZH1', g.gwaerzh1)} · {gwrLabel('GENH1', g.genh1)}
            </dd>
            <dt>Warmwasser</dt>
            <dd>
              {gwrLabel('GWAERZW1', g.gwaerzw1)} · {gwrLabel('GENW1', g.genw1)}
            </dd>
          </dl>
          <p className="xsmall muted" style={{ marginTop: 12 }}>
            Quelle: Eidg. Gebäude- und Wohnungsregister (BFS) über geo.admin.ch, Stand {g.gexpdat ?? '–'}; Codes: {GWR_CODE_SOURCE}. Nur informativ, fliesst in keine Berechnung ein.
          </p>
        </>
      )}
    </div>
  );
}

export function PeriodLabel({ cfg }: { cfg: AnalysisConfig }) {
  return (
    <span>
      {cfg.periodStart}–{cfg.periodEnd}
    </span>
  );
}

export { fmtDate };
